import type { FishCropImageStatus, LiceFilterClass, WelfareFilterClass } from '@restDataAccess';
import type { WelfareFishCandidate } from '@scaleaq/sitecontrol-lib';
import type { SlideImage } from 'yet-another-react-lightbox';

import { DateTime } from 'luxon';

/** A lice box resolved to the displayed crop's pixel space, ready to draw. */
export type LiceBox = {
  bbox: BoundingBox;
  type?: FishWelfareResponse;
};

export type OrbitOneGalleryFilterClass = LiceFilterClass | WelfareFilterClass;

/** One localized breakdown entry; `score` is set for welfare results that carry one. */
export type LocalizedObservationBreakdownEntry = {
  id: string;
  label: string;
  /** Breakdown category; decides the colour that matches this category's box in the overlay. */
  category: FishWelfareResponse;
  score?: number;
};

/** Lightbox image slide carrying the observation so its lice boxes can be overlaid. */
export type FishObservationSlide = SlideImage & {
  observation: WelfareFishCandidate;
  fetchingStatus: FishCropImageStatus;
  /**
   * `preview` while the lightbox still shows the thumbnail, `ready` once the original crop is
   * available, `error` when that original failed after a thumbnail was already shown.
   */
  fullResolutionState: 'preview' | 'ready' | 'error';
};

/** Per-camera filter selection for the lice observation gallery. */
export interface OrbitOneGalleryFilterState {
  /** Selected lice classes; all selected (the default) means no filter — return all fish. */
  filters: OrbitOneGalleryFilterClass[];
  date: DateTime;
}

/** Bounding box tuple `[x1, y1, x2, y2]` in the crop's native pixel space, for the lice overlay. */
export type BoundingBox = [number, number, number, number];

/** Camera whose crop is displayed for an observation. */
export type DisplayCamera = 'primary' | 'secondary';

export type CameraFilterType = 'lice' | 'welfare';

// The responses have different casing from FilterType, so we cannot use sitecontrol-lib type
export enum FishWelfareResponse {
  Mobile = 'mobile',
  Female = 'female',
  FemaleEggstring = 'female_eggstring',
  ScottishLice = 'scottish_lice',
  ActiveWound = 'active_wound',
  HealedWound = 'healed_wound',
  SnoutDamage = 'snout_damage',
  ScaleLoss = 'scale_loss',
}
